import { Box, Field, HStack, Stack } from '@chakra-ui/react';
import VText from '../VText';
import type { Font } from 'opentype.js';
import {
  type ChangeEvent,
  type CSSProperties,
  type PointerEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import VButton from '../VButton';
import VInput from '../VInput';
import { loadSignatureFont } from './VSignature.fonts';
import {
  DRAW_MODE,
  EMPTY_PATH,
  LIVE_STROKE_TEST_ID,
  SIGNATURE_PAD_ASPECT_RATIO,
  SIGNATURE_PAD_WIDTH,
  SIGNATURE_VIEW_BOX,
  STROKE_TEST_ID,
  toSignatureFile,
  toStrokePath,
  toSvgMarkup,
  toTypedNamePath,
  TYPE_MODE,
  TYPED_NAME_MAX_LENGTH,
  TYPED_NAME_TEST_ID,
  type TPoint,
  type TSignatureMode,
  type TStroke,
} from './VSignature.helpers';
import VTabs, { type VTabsOption } from '../VTabs';

const PAD_STYLE: CSSProperties = { width: '100%', height: '100%', touchAction: 'none' }; // temp to be updated

export type TSignatureHelperText = Partial<Record<TSignatureMode, string>>;

export interface EditableSignatureProps {
  onChange: (signature: File | null) => void;
  prefixHelperText?: TSignatureHelperText;
  invalid?: boolean;
  typedNameLabel?: string;
}

export const EditableSignature = ({
  onChange,
  prefixHelperText,
  invalid,
  typedNameLabel = 'Type your signature',
}: EditableSignatureProps) => {
  const signature = useRef<SVGSVGElement>(null);
  const livePath = useRef<SVGPathElement>(null);
  const strokeInProgress = useRef<TStroke | null>(null);
  const [mode, setMode] = useState<TSignatureMode>(DRAW_MODE);
  const [strokes, setStrokes] = useState<TStroke[]>([]);
  const [typedName, setTypedName] = useState('');
  const [font, setFont] = useState<Font | null>(null);
  const latestOnChange = useRef(onChange);
  const typedNameInputId = useId();

  const isDrawMode = mode === DRAW_MODE;
  const strokePaths = useMemo(() => strokes.map(toStrokePath), [strokes]);
  const typedNamePath = font ? toTypedNamePath(font, typedName) : EMPTY_PATH;

  useEffect(() => {
    if (isDrawMode) {
      return;
    }

    const load = async () => setFont(await loadSignatureFont());

    load();
  }, [isDrawMode]);

  useEffect(() => {
    latestOnChange.current = onChange;
  });

  useEffect(() => {
    const pad = signature.current;
    const hasSignature = isDrawMode ? strokePaths.length > 0 : typedNamePath !== EMPTY_PATH;

    if (!pad || !hasSignature) {
      latestOnChange.current(null);

      return;
    }

    latestOnChange.current(toSignatureFile(toSvgMarkup(pad), Date.now()));
  }, [isDrawMode, strokePaths, typedNamePath]);

  const pointFrom = (event: PointerEvent<SVGSVGElement>): TPoint => {
    const { left, top, width } = event.currentTarget.getBoundingClientRect();
    const scale = width > 0 ? SIGNATURE_PAD_WIDTH / width : 1;

    return [(event.clientX - left) * scale, (event.clientY - top) * scale];
  };

  const drawLivePath = (stroke: TStroke) => {
    livePath.current?.setAttribute('d', toStrokePath(stroke));
  };

  const clearLivePath = () => {
    livePath.current?.setAttribute('d', EMPTY_PATH);
  };

  const startStroke = (event: PointerEvent<SVGSVGElement>) => {
    if (!isDrawMode) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);

    const stroke: TStroke = [pointFrom(event)];

    strokeInProgress.current = stroke;
    drawLivePath(stroke);
  };

  const extendStroke = (event: PointerEvent<SVGSVGElement>) => {
    const stroke = strokeInProgress.current;

    if (!stroke) {
      return;
    }

    const extended = [...stroke, pointFrom(event)];

    strokeInProgress.current = extended;
    drawLivePath(extended);
  };

  const endStroke = () => {
    const stroke = strokeInProgress.current;

    if (!stroke) {
      return;
    }

    strokeInProgress.current = null;
    clearLivePath();
    setStrokes((completed) => [...completed, stroke]);
  };

  const clear = () => {
    strokeInProgress.current = null;
    clearLivePath();
    setStrokes([]);
    setTypedName('');
  };

  const selectMode = ({ value }: { value: string }) =>
    setMode(value === TYPE_MODE ? TYPE_MODE : DRAW_MODE);

  const drawHelperText = prefixHelperText?.[DRAW_MODE];
  const typeHelperText = prefixHelperText?.[TYPE_MODE];

  const drawPanel = drawHelperText ? <VText color="fg.muted">{drawHelperText}</VText> : null;

  const typePanel = (
    <Stack gap="4">
      {typeHelperText && <VText color="fg.muted">{typeHelperText}</VText>}

      <Field.Root required>
        <Field.Label htmlFor={typedNameInputId} fontWeight="semibold">
          {typedNameLabel}
        </Field.Label>

        <VInput
          id={typedNameInputId}
          value={typedName}
          maxLength={TYPED_NAME_MAX_LENGTH}
          placeholder="Type your name"
          onChange={(event: ChangeEvent<HTMLInputElement>) => setTypedName(event.target.value)}
        />
      </Field.Root>
    </Stack>
  );

  const signatureModes: VTabsOption[] = [
    { value: DRAW_MODE, label: 'Draw', content: isDrawMode ? drawPanel : null },
    { value: TYPE_MODE, label: 'Type', content: isDrawMode ? null : typePanel },
  ];

  return (
    <Stack gap="4">
      <VTabs options={signatureModes} value={mode} onValueChange={selectMode} />

      <Box
        width="100%"
        aspectRatio={SIGNATURE_PAD_ASPECT_RATIO}
        borderWidth="1px"
        borderColor={invalid ? 'border.error' : 'border'}
        rounded="md"
      >
        <svg
          ref={signature}
          viewBox={SIGNATURE_VIEW_BOX}
          aria-label="Signature"
          aria-invalid={invalid}
          style={PAD_STYLE}
          onPointerDown={startStroke}
          onPointerMove={extendStroke}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
        >
          {!isDrawMode && <title>{typedName}</title>}

          {isDrawMode ? (
            <>
              {strokePaths.map((path, index) => (
                <path key={index} d={path} data-testid={STROKE_TEST_ID} />
              ))}

              <path ref={livePath} data-testid={LIVE_STROKE_TEST_ID} />
            </>
          ) : (
            <path d={typedNamePath} data-testid={TYPED_NAME_TEST_ID} />
          )}
        </svg>
      </Box>
      <HStack justifyContent={'end'}>
        <VButton color="tertiary" onClick={clear}>
          Clear
        </VButton>
      </HStack>
    </Stack>
  );
};
